// Primary navigation: a bottom tab bar on phones (thumb reach), a row of
// tabs under the header on tablets. The current section is marked with
// aria-current and a pink underline. It shows the main four sections the
// person can use, then More, which lists every other section (the same list
// as the laptop sidebar). Tabs size to their labels, so "Candidates" fits
// beside the others on a 320px phone.
import { ChatCircleText, DotsThreeOutline, IdentificationCard, MagnifyingGlass } from "@phosphor-icons/react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import type { Me } from "../lib/api.ts";
import { openFeedback } from "./feedback-button.tsx";
import { openQuickFind } from "./quick-find.tsx";
import { type Section, sectionsFor, type To } from "./sections.ts";
import { Drawer } from "./ui.tsx";

/** Most used first; the tab bar takes the first four the person can use. */
const TAB_ORDER: To[] = ["/", "/tasks", "/candidates", "/bookings", "/clients"];
const TABS = 4;

const tabClass =
	"group flex min-h-14 w-full flex-col items-center justify-center gap-0.5 px-1 text-sm font-semibold text-ink-soft data-[status=active]:text-ink sm:min-h-12 sm:flex-row sm:gap-2 sm:rounded-full sm:px-4 sm:text-base sm:data-[status=active]:bg-card";
const rowClass =
	"group flex min-h-12 w-full items-center gap-3 rounded-xl px-3 text-left font-semibold text-ink hover:bg-mint/25 data-[status=active]:bg-mint/40";

export function Nav({ me }: { me: Me }) {
	const [more, setMore] = useState(false);
	const path = useRouterState({ select: (s) => s.location.pathname });
	const groups = sectionsFor(me);
	const all = groups.flatMap((g) => g.items);
	const tabs = TAB_ORDER.map((to) => all.find((s) => s.to === to))
		.filter((s): s is Section => Boolean(s))
		.slice(0, TABS);
	const rest = groups
		.map((g) => ({ ...g, items: g.items.filter((s) => !tabs.includes(s)) }))
		.filter((g) => g.items.length);
	// More is the current section when the page on screen is one of its items.
	const inMore =
		rest.flatMap((g) => g.items).some((s) => path === s.to || path.startsWith(`${s.to}/`)) || path === "/account";

	return (
		<nav
			aria-label="Main"
			className="fixed inset-x-0 bottom-0 z-20 border-t border-rule bg-card pb-[env(safe-area-inset-bottom)] sm:static sm:border-0 sm:bg-transparent sm:pb-0 lg:hidden"
		>
			<ul className="mx-auto flex max-w-[64rem] sm:gap-2 sm:px-4">
				{tabs.map(({ to, label, Icon, exact }) => (
					<li key={to} className="flex-auto sm:flex-none">
						<Link to={to} activeOptions={{ exact: Boolean(exact) }} className={tabClass}>
							<Icon size={24} weight="fill" aria-hidden="true" className="group-data-[status=active]:text-pink" />
							<span className="border-b-2 border-transparent group-data-[status=active]:border-pink">{label}</span>
						</Link>
					</li>
				))}
				<li className="flex-auto sm:flex-none">
					<button
						type="button"
						aria-haspopup="dialog"
						aria-current={inMore ? "page" : undefined}
						data-status={inMore ? "active" : undefined}
						onClick={() => setMore(true)}
						className={tabClass}
					>
						<DotsThreeOutline
							size={24}
							weight="fill"
							aria-hidden="true"
							className="group-data-[status=active]:text-pink"
						/>
						<span className="border-b-2 border-transparent group-data-[status=active]:border-pink">More</span>
					</button>
				</li>
			</ul>
			{more ? (
				<Drawer title="More" onClose={() => setMore(false)}>
					<button
						type="button"
						onClick={() => {
							setMore(false);
							openQuickFind();
						}}
						className={rowClass}
					>
						<MagnifyingGlass size={22} weight="bold" aria-hidden="true" className="shrink-0 text-purple" />
						Find anything
					</button>
					{rest.map((group, i) => (
						<div key={group.label} className="mt-2 border-t border-rule pt-3">
							<p id={`more-group-${i}`} className="px-3 pb-1 text-sm font-bold tracking-[0.06em] text-label uppercase">
								{group.label}
							</p>
							<ul aria-labelledby={`more-group-${i}`} className="space-y-1">
								{group.items.map(({ to, label, Icon, exact }) => (
									<li key={to}>
										<Link
											to={to}
											activeOptions={{ exact: Boolean(exact) }}
											onClick={() => setMore(false)}
											className={rowClass}
										>
											<Icon
												size={22}
												weight="fill"
												aria-hidden="true"
												className="shrink-0 text-purple group-data-[status=active]:text-pink"
											/>
											{label}
										</Link>
									</li>
								))}
							</ul>
						</div>
					))}
					<div className="mt-2 border-t border-rule pt-2">
						<Link to="/account" onClick={() => setMore(false)} className={rowClass}>
							<IdentificationCard size={22} weight="fill" aria-hidden="true" className="shrink-0 text-purple" />
							Your account
						</Link>
						{/* Phones only: there's no floating Feedback button over the page here. */}
						<button
							type="button"
							onClick={() => {
								setMore(false);
								openFeedback();
							}}
							className={`${rowClass} sm:hidden`}
						>
							<ChatCircleText size={22} weight="fill" aria-hidden="true" className="shrink-0 text-purple" />
							Feedback
						</button>
					</div>
				</Drawer>
			) : null}
		</nav>
	);
}
